import React from 'react';
import { useThemeColors, useThemeStyles } from '../contexts';

interface ThemedButtonProps {
  variant?: 'primary' | 'secondary' | 'accent';
  size?: 'small' | 'medium' | 'large';
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}

export const ThemedButton: React.FC<ThemedButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  children,
  onClick,
  disabled = false,
  className = '',
}) => {
  const { primary, secondary, accent } = useThemeColors();
  const styles = useThemeStyles();

  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: primary,
          color: 'white',
          border: `2px solid ${primary}`,
        };
      case 'secondary':
        return {
          backgroundColor: 'transparent',
          color: secondary,
          border: `2px solid ${secondary}`,
        };
      case 'accent':
        return {
          backgroundColor: accent,
          color: 'white',
          border: `2px solid ${accent}`,
        };
      default:
        return styles.primaryButton;
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          padding: '0.25rem 0.75rem',
          fontSize: '0.875rem',
        };
      case 'medium':
        return {
          padding: '0.5rem 1rem',
          fontSize: '1rem',
        };
      case 'large':
        return {
          padding: '0.75rem 1.5rem',
          fontSize: '1.125rem',
        };
      default:
        return {
          padding: '0.5rem 1rem',
          fontSize: '1rem',
        };
    }
  };

  const getHoverStyles = () => {
    if (disabled) return {};

    switch (variant) {
      case 'primary':
        return {
          opacity: 0.9,
          transform: 'scale(0.98)',
        };
      case 'secondary':
        return {
          backgroundColor: secondary,
          color: 'white',
        };
      case 'accent':
        return {
          opacity: 0.9,
          transform: 'scale(0.98)',
        };
      default:
        return { opacity: 0.9 };
    }
  };

  const baseStyles = {
    borderRadius: '0.5rem',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    transition: 'all 0.2s ease-in-out',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    outline: 'none',
  };

  return (
    <button
      className={`themed-button ${className}`}
      style={{
        ...baseStyles,
        ...getVariantStyles(),
        ...getSizeStyles(),
      }}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      onMouseEnter={(e) => {
        if (!disabled) {
          Object.assign(e.currentTarget.style, getHoverStyles());
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          Object.assign(e.currentTarget.style, {
            ...getVariantStyles(),
            ...getSizeStyles(),
          });
        }
      }}
    >
      {children}
    </button>
  );
};

export default ThemedButton;
